import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../../utils/cropImage';

interface ImageCropperModalProps {
    isOpen: boolean;
    imageSrc: string;
    onCropComplete: (croppedBase64: string) => void;
    onCancel: () => void;
}

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({ isOpen, imageSrc, onCropComplete, onCancel }) => {
    const { t } = useTranslation();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [livePreview, setLivePreview] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const onCropCompleteHandler = useCallback((_: any, croppedAreaPixelsData: any) => {
        setCroppedAreaPixels(croppedAreaPixelsData);
    }, []);

    useEffect(() => {
        const updatePreview = async () => {
            if (croppedAreaPixels && imageSrc) {
                try {
                    const preview = await getCroppedImg(imageSrc, croppedAreaPixels);
                    setLivePreview(preview);
                    setPreviewError(null);
                } catch(e: any) { 
                    setLivePreview(null);
                    setPreviewError(e?.message || t('common.imageCropper.errorCanvas'));
                }
            }
        };
        // Debounce simple
        const timer = setTimeout(updatePreview, 300);
        return () => clearTimeout(timer);
    }, [croppedAreaPixels, imageSrc]);

    const handleSave = async () => {
        try {
            if (!croppedAreaPixels) return;
            const croppedImage = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
            );
            onCropComplete(livePreview || croppedImage);
        } catch (e: any) {
            console.error("Error cropping image:", e);
            alert(t('common.imageCropper.browserError', { message: e.message }));
            onCropComplete(imageSrc);
        }
    };

    if (!isOpen || !imageSrc) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '90%', maxWidth: '600px', background: 'var(--color-panel-bg)', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--color-panel-border)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
                <div style={{ padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: 900, letterSpacing: '1px' }}>{t('common.imageCropper.cropAvatar')}</h3>
                    <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1 }} title={t('common.imageCropper.cancelTitle')}>×</button>
                </div>
                
                <div style={{ position: 'relative', width: '100%', height: '400px', background: 'var(--color-bg-base)', display: 'flex' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onCropComplete={onCropCompleteHandler}
                            onZoomChange={setZoom}
                        />
                    </div>
                    {/* Live Preview Bar */}
                    <div style={{ width: '150px', background: '#111', borderLeft: '1px solid #333', padding: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '0.65rem', color: '#888', fontWeight: 'bold' }}>{t('common.imageCropper.finalPreview')}</span>
                        <div style={{ 
                            width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#222',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '2px solid var(--color-role-controller)', overflow: 'hidden'
                        }}>
                            {livePreview ? (
                                <img src={livePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : previewError ? (
                                <span style={{ color: 'red', fontSize: '0.5rem', textAlign: 'center', padding: '5px' }}>{previewError}</span>
                            ) : null}
                        </div>
                        <span style={{ fontSize: '0.6rem', color: '#555', marginTop: 'auto', textAlign: 'center' }}>
                            {croppedAreaPixels ? `${Math.round(croppedAreaPixels.width)}x${Math.round(croppedAreaPixels.height)}px` : '...'}
                        </span>
                    </div>
                </div>
                
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', background: 'rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <span style={{ fontSize: '1.2rem', opacity: 0.5 }}>-</span>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.05}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(Number(e.target.value))}
                            style={{ flex: 1, accentColor: 'var(--color-role-controller)' }}
                        />
                        <span style={{ fontSize: '1.2rem', opacity: 0.8 }}>+</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button onClick={onCancel} style={{ padding: '10px 20px', background: 'transparent', border: '1px solid #444', color: '#ccc', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>{t('common.imageCropper.cancel')}</button>
                        <button onClick={handleSave} style={{ padding: '10px 20px', background: 'var(--color-role-controller)', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>{t('common.imageCropper.applyCrop')}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};
